import type { BadgeChannelType } from '../../components/dialogs/ChannelBadge';
import type { ContactDto } from '../../api/types';
import { ChannelBadge } from '../dialogs/ChannelBadge';
import { Avatar } from '../ui/avatar';
import { Badge } from '../ui/badge';

export interface KanbanCardProps {
  contact: ContactDto;
  onDragStart: (e: React.DragEvent, contact: ContactDto) => void;
  onClick: (contact: ContactDto) => void;
  /** Channel type for `contact.sourceChannelId`, resolved once by KanbanBoard via GET /channels. */
  channelType?: BadgeChannelType;
}

function contactName(c: ContactDto): string {
  const name = [c.firstName, c.lastName].filter(Boolean).join(' ');
  return name || `VK ID ${c.vkUserId}`;
}

export function KanbanCard({ contact, onDragStart, onClick, channelType }: KanbanCardProps) {
  const name = contactName(contact);
  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, contact)}
      onClick={() => onClick(contact)}
      data-testid={`kanban-card-${contact.id}`}
      className="flex cursor-grab flex-col gap-1.5 rounded border border-border bg-card p-2 text-sm shadow-sm active:cursor-grabbing"
    >
      <div className="flex items-center gap-2">
        <Avatar name={name} photoUrl={contact.photoUrl} size="sm" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {channelType && <ChannelBadge type={channelType} />}
            <span className="truncate font-medium">{name}</span>
          </div>
          <div className="text-xs text-muted-fg">VK ID {contact.vkUserId}</div>
        </div>
        {contact.awaitingHuman && (
          <Badge tone="warning" aria-label="ждёт ответа" title="Ждёт ответа человека">
            ждёт ответа
          </Badge>
        )}
      </div>
      {/* TODO(server): GET /contacts has no last-message preview yet — shown once it does. */}
      {contact.lastMessage?.text && <p className="truncate text-xs text-muted-fg">{contact.lastMessage.text}</p>}
      {contact.tags.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map((t) => (
            <Badge key={t}>{t}</Badge>
          ))}
        </div>
      )}
    </div>
  );
}
